import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import axios, { AxiosError } from 'axios'
import { getApiBaseUrlWithoutV1 } from '@/setup/SetupAxios'

/**
 * The RTK Query base query: the app's default axios instance, so the JWT request interceptor and the 401 logout apply
 * as everywhere else, against the API root (`/<domain>/<resource>/v<N>` routes, not `/v1`). Unwraps the standard
 * `{ success, data }` body. No HTTP caching: the cache is RTK Query's.
 */

interface ApiRequest {
    url: string
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    params?: Record<string, string | number | boolean | undefined>
    body?: unknown
}

export interface ApiError {
    status: number | 'FETCH_ERROR'
    message: string
}

interface ApiEnvelope<T> {
    success?: boolean
    data?: T
    message?: string
}

const definedParams = (params: ApiRequest['params']): Record<string, string | number | boolean> | undefined => {
    if (!params) return undefined
    const entries = Object.entries(params).filter((entry): entry is [string, string | number | boolean] => entry[1] !== undefined)
    return entries.length ? Object.fromEntries(entries) : undefined
}

/** One request, its envelope's `data` on success (absent when the body carried none), an `ApiError` (never thrown) otherwise. */
export const requestApi = async <T>(request: ApiRequest, signal?: AbortSignal): Promise<{ data: T | undefined } | { error: ApiError }> => {
    try {
        const response = await axios.request<ApiEnvelope<T>>({
            url: request.url,
            method: request.method ?? 'GET',
            params: definedParams(request.params),
            data: request.body,
            baseURL: getApiBaseUrlWithoutV1(),
            signal,
        })
        return { data: response.data?.data }
    } catch (error) {
        const axiosError = error instanceof AxiosError ? error : undefined
        const body: ApiEnvelope<unknown> | undefined = axiosError?.response?.data
        return {
            error: {
                status: axiosError?.response?.status ?? 'FETCH_ERROR',
                message: body?.message || (error instanceof Error ? error.message : 'Request failed'),
            },
        }
    }
}

export const axiosBaseQuery =
    (): BaseQueryFn<ApiRequest, unknown, ApiError> =>
    (request, { signal }) =>
        requestApi(request, signal)
